import { motion } from "framer-motion";
import {
  Braces,
  Bot,
  Users,
  RotateCcw,
  Smartphone,
  Sparkles,
  Clock4,
} from "lucide-react";
import SectionLabel from "@/components/sections/SectionLabel";

const features = [
  {
    icon: Braces,
    title: "Code-aware",
    body: "Frontend + Backend understanding — not just URLs and selectors.",
  },
  {
    icon: Bot,
    title: "Autonomous",
    body: "No script maintenance. The agent rewrites its strategy as your product evolves.",
  },
  {
    icon: Users,
    title: "Real User Behaviour",
    body: "Not robotic automation. Curious, persistent, and creative like a power user.",
  },
  {
    icon: RotateCcw,
    title: "Unlimited Regression Testing",
    body: "Every commit, automatically. Catch silent regressions before they ship.",
  },
  {
    icon: Smartphone,
    title: "Cross Device",
    body: "One click validates every screen size, browser and network condition.",
  },
  {
    icon: Sparkles,
    title: "Learns Continuously",
    body: "Gets smarter every release. Your historical bugs become future safety nets.",
  },
  {
    icon: Clock4,
    title: "Runs Anytime",
    body: "Nightly, before merge, after deploy or continuously — your choice.",
  },
];

export default function FeatureGrid() {
  return (
    <section
      data-testid="feature-grid-section"
      className="relative py-24 md:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <SectionLabel>Why teams love Liskov AI</SectionLabel>
          <h2 className="font-display font-semibold mt-4 text-balance text-4xl sm:text-5xl md:text-[56px] leading-[1.05] tracking-[-0.025em]">
            Engineering excellence,{" "}
            <span className="text-muted-foreground">on autopilot.</span>
          </h2>
        </div>

        <div
          data-testid="feature-grid"
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              data-testid={`feature-card-${i + 1}`}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="premium-card p-6 md:p-7 group"
            >
              <div className="h-10 w-10 rounded-xl bg-foreground/[0.04] border border-border inline-flex items-center justify-center text-foreground/80 group-hover:text-[hsl(var(--accent))] group-hover:border-[hsl(var(--accent)/0.4)] transition-colors">
                <f.icon className="h-4.5 w-4.5" strokeWidth={1.7} />
              </div>
              <h3 className="mt-5 font-display text-lg font-medium tracking-tight">
                {f.title}
              </h3>
              <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed">
                {f.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
